import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getIsManageSubscriptionDialogOpen, setIsManageSubscriptionDialogOpen } from "../reducers/subscription/subscriptionReducer";
import { SubscriptionProviderInstance } from "../@shared/services/SubscriptionProvider";
import { User } from "../@shared/Models/Models";
import { NotificationModel, NotificationModelType } from "../reducers/main/mainModel";
import { onThrowAlert } from "../sagas/dataSaga/dataSagaActions";
import { getIsAuthorized, setSeats, setSeatsIsLoading } from "../reducers/main/mainReducer";


export type useManageSubscriptionProps = {

}

export type useManageSubscriptionResult = {
    onAssign: (id: string) => Promise<void>,
    onUnassign: (id: string) => Promise<void>,
    onLoadSeats: () => Promise<void>,
    isDialogOpen: boolean,
    setIsDialogOpen: (value: boolean) => void,
    onSearch: (value: string) => void,
    searchData: User[],
    isSearching: boolean,
    searchValue: string,
    setSearchValue: (value: string) => void
}

export const useManageSubscription = (): useManageSubscriptionResult => {
    const dispatch = useDispatch();
    const isDialogOpen = useSelector(getIsManageSubscriptionDialogOpen);
    const [isSearching, setIsSearching] = useState(false);
    const [searchValue, setSearchValue] = useState<string>("");
    const [searchData, setSearchData] = useState<User[]>([]);
    const user = useSelector(getIsAuthorized);
    const userLicense = useMemo(() => user?.license, [user?.license]);
    const isOwner = useMemo(() =>
        userLicense?.licensedUsers.find(x => x.externalUserId === user?.id)?.isOwner
        , [user?.id, userLicense?.licensedUsers])

    const setIsDialogOpen = useCallback((value: boolean) => {
        dispatch(setIsManageSubscriptionDialogOpen(value));
    }, [dispatch]);
    const subscriptionProviderInstance = useMemo(() => SubscriptionProviderInstance.getInstance(), [])

    const onLoadSeats = useCallback(async () => {
        if (!isOwner) {
            return;
        }
        dispatch(setSeatsIsLoading(true));
        const response = await subscriptionProviderInstance.getSeats();
        if (response.status === 200) {
            dispatch(setSeats(response.data));
        }
        dispatch(setSeatsIsLoading(false));
    }, [dispatch, isOwner, subscriptionProviderInstance]);

    const onAssign = useCallback(async (id: string) => {
        var res = await subscriptionProviderInstance.assign(id);
        if (res.status !== 200) {
            dispatch(onThrowAlert({
                message: res.data,
                lifeTime: 3000,
                type: NotificationModelType.Error
            } as NotificationModel));
        }
        else {
            await onLoadSeats();
            dispatch(onThrowAlert({
                message: "The user was assigned to the license",
                lifeTime: 3000,
                type: NotificationModelType.Success
            } as NotificationModel));
        }

    }, [dispatch, onLoadSeats, subscriptionProviderInstance]);

    const onUnassign = useCallback(async (id: string) => {
        var res = await subscriptionProviderInstance.unassign(id);
        if (res.status !== 200) {
            dispatch(onThrowAlert({
                message: res.data,
                lifeTime: 3000,
                type: NotificationModelType.Error
            } as NotificationModel));
        }
        else {
            await onLoadSeats();
            dispatch(onThrowAlert({
                message: "The user was unassigned from the license",
                lifeTime: 3000,
                type: NotificationModelType.Warning
            } as NotificationModel));
        }

    }, [dispatch, onLoadSeats, subscriptionProviderInstance]);

    const onSearch = useCallback(async (search: string) => {
        setIsSearching(true);
        const response = await subscriptionProviderInstance.search(search);
        if (response.status === 200) {
            setSearchData(response.data);
        }
        setIsSearching(false);
    }, [subscriptionProviderInstance]);

    useEffect(() => {
        if (user?.license?.isSupportsSeats) {
            onLoadSeats();
        }
    }, [onLoadSeats, user?.license?.isSupportsSeats])

    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchValue) {
                onSearch(searchValue)
            }
        }, 1000)
        return () => {
            clearTimeout(timer)
        }
    }, [onSearch, searchValue])

    return {
        onLoadSeats,
        onAssign,
        onUnassign,
        isDialogOpen,
        setIsDialogOpen,
        onSearch,
        searchData,
        searchValue,
        isSearching,
        setSearchValue
    }
}